import { SERVER_URL } from '../utils/constants';

/**
 * Base URL for `/api/explorer/*` (same `{SERVER_URL}/api/...` pattern as roomService).
 * Deployed REACT_APP_SERVER_URL often ends with `/api` (gateway), so this becomes `…/api/api`.
 */
export function meetgeekApiRoot() {
  const raw = String(SERVER_URL || '').replace(/\/$/, '');
  if (/\/api(?:\/api)+$/.test(raw)) return raw;
  return `${raw}/api`;
}

function scopeParams({ companyId, recruiterId }) {
  const params = new URLSearchParams();
  if (companyId) params.set('company_id', String(companyId));
  if (recruiterId) params.set('recruiter_id', String(recruiterId));
  return params;
}

async function getJSON(path, scope) {
  const params = scopeParams(scope);
  const qs = params.toString();
  const url = `${meetgeekApiRoot()}${path}${qs ? `?${qs}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText} ${detail}`.trim());
  }
  return res.json();
}

export function listMeetings(
  {
    q,
    status,
    dateFrom,
    dateTo,
    host,
    duration,
    limit = 50,
    skip = 0,
    companyId,
    recruiterId,
  } = {}
) {
  if (!companyId || !recruiterId) {
    return Promise.reject(
      new Error('company_id and recruiter_id are required')
    );
  }
  const params = scopeParams({ companyId, recruiterId });
  const trimmedQ = String(q || '').trim();
  if (trimmedQ) params.set('q', trimmedQ);
  if (status) params.set('status', status);
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  if (host && host !== 'all') params.set('host', String(host));
  if (duration && duration !== 'any') params.set('duration', String(duration));
  params.set('limit', String(limit));
  params.set('skip', String(skip));
  const url = `${meetgeekApiRoot()}/explorer/meetings?${params.toString()}`;
  return fetch(url).then(async (res) => {
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`${res.status} ${res.statusText} ${detail}`.trim());
    }
    return res.json();
  });
}

/** Full-scope hosts + stats for company/recruiter (not page-limited). */
export function getMeetingFacets({ companyId, recruiterId, status = 'ended' } = {}) {
  if (!companyId || !recruiterId) {
    return Promise.reject(
      new Error('company_id and recruiter_id are required')
    );
  }
  const params = scopeParams({ companyId, recruiterId });
  if (status) params.set('status', status);
  const url = `${meetgeekApiRoot()}/explorer/meetings/facets?${params.toString()}`;
  return fetch(url).then(async (res) => {
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`${res.status} ${res.statusText} ${detail}`.trim());
    }
    return res.json();
  });
}

export function getMeeting(roomName, scope) {
  return getJSON(
    `/explorer/meetings/${encodeURIComponent(roomName)}`,
    scope
  );
}

export function mediaUrl(roomName, kind, scope) {
  const params = scopeParams(scope);
  const qs = params.toString();
  return `${meetgeekApiRoot()}/explorer/meetings/${encodeURIComponent(roomName)}/media/${kind}${qs ? `?${qs}` : ''}`;
}

export async function getText(roomName, kind, scope) {
  const params = scopeParams(scope);
  const qs = params.toString();
  const res = await fetch(
    `${meetgeekApiRoot()}/explorer/meetings/${encodeURIComponent(roomName)}/text/${kind}${qs ? `?${qs}` : ''}`
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText} ${detail}`.trim());
  }
  return res.text();
}
